/**
 * Fixed-timestep simulation loop with render interpolation.
 *
 * - Simulation (`fixedUpdate`) always advances in exact FIXED_DT steps -> deterministic.
 * - Rendering (`update`) runs once per animation frame with `alpha` in [0,1): how far we are
 *   between the previous and current simulation state. Views lerp prev->current by alpha.
 *
 * The class is time-source agnostic: call `advance(realDtSeconds)` from rAF, or `stepFrames(n)`
 * from tests to advance the simulation deterministically.
 */
export const FIXED_HZ = 60;
export const FIXED_DT = 1 / FIXED_HZ;

export interface LoopCallbacks {
  /** Called before each batch of fixed steps (e.g. input polling). */
  beforeFixed?(): void;
  fixedUpdate(dt: number): void;
  update(dt: number, alpha: number): void;
}

export class FixedStepLoop {
  /** Accumulated unsimulated real time, seconds. */
  private accumulator = 0;
  /** Total simulated time, seconds. */
  simTime = 0;
  /** Number of fixed steps executed so far. */
  frame = 0;
  /** Max frame delta accepted (prevents spiral of death after tab switches). */
  maxFrameDt = 0.25;
  /** Max fixed steps per rendered frame. */
  maxStepsPerFrame = 6;
  /** Simulation speed multiplier (debug / slow-mo). */
  timeScale = 1;
  /** When true, fixed steps are skipped (rendering still runs). */
  paused = false;
  /** Fixed steps executed during the last `advance` call. */
  lastSteps = 0;

  constructor(private readonly cb: LoopCallbacks) {}

  /** Advance by real elapsed time. Returns number of fixed steps executed. */
  advance(realDt: number): number {
    const dt = Math.min(Math.max(realDt, 0), this.maxFrameDt);
    let steps = 0;
    if (!this.paused) {
      this.accumulator += dt * this.timeScale;
      if (this.accumulator >= FIXED_DT) this.cb.beforeFixed?.();
      while (this.accumulator >= FIXED_DT && steps < this.maxStepsPerFrame) {
        this.step();
        this.accumulator -= FIXED_DT;
        steps++;
      }
      // Drop backlog we could not simulate instead of accumulating forever.
      if (steps >= this.maxStepsPerFrame) this.accumulator = Math.min(this.accumulator, FIXED_DT);
    }
    this.lastSteps = steps;
    this.cb.update(dt, this.paused ? 1 : this.accumulator / FIXED_DT);
    return steps;
  }

  /** Run exactly n fixed steps immediately (ignores pause). Used by tests and window.__bh. */
  stepFrames(n: number): void {
    for (let i = 0; i < n; i++) {
      this.cb.beforeFixed?.();
      this.step();
    }
    this.cb.update(n * FIXED_DT, 1);
  }

  private step(): void {
    this.cb.fixedUpdate(FIXED_DT);
    this.simTime += FIXED_DT;
    this.frame++;
  }
}
